import React, { useEffect, useState } from "react";
import axios from "axios";
import API_BASE_URL from "../../config";

const PROVIDERS = [
  { value: "brevo", label: "Brevo (Sendinblue)", fields: ["api_key"] },
  { value: "mailgun", label: "Mailgun", fields: ["api_key", "domain", "smtp_user"] },
  { value: "resend", label: "Resend", fields: ["api_key"] },
  { value: "smtp", label: "Custom SMTP", fields: ["smtp_host", "smtp_port", "smtp_user", "smtp_pass", "smtp_secure"] },
];

const FIELD_LABELS = {
  api_key: "API Key",
  domain: "Sending Domain",
  smtp_host: "SMTP Host",
  smtp_port: "SMTP Port",
  smtp_user: "SMTP Username / Login Email",
  smtp_pass: "SMTP Password",
  smtp_secure: "Use SSL/TLS",
};

const FIELD_PLACEHOLDERS = {
  api_key: "Enter API key",
  domain: "e.g. pairfon.com",
  smtp_host: "e.g. smtp.gmail.com",
  smtp_port: "587",
  smtp_user: "e.g. user@example.com",
  smtp_pass: "Enter SMTP password",
};

const PROVIDER_HELP = {
  brevo: "Sign up at brevo.com. Go to SMTP & API > API Keys to get your API key (starts with xkeysib-).",
  mailgun: "Sign up at mailgun.com. Get your API key from API Security. Add and verify your sending domain.",
  resend: "Sign up at resend.com. Get your API key from the dashboard. Add and verify your domain.",
  smtp: "Enter your SMTP server details. Works with any email provider (Gmail, Outlook, etc).",
};

export default function OpsEmailSettings() {
  const token = localStorage.getItem("userToken");
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [testEmail, setTestEmail] = useState("");
  const [currentSettings, setCurrentSettings] = useState(null);

  const [form, setForm] = useState({
    provider: "brevo",
    api_key: "",
    domain: "",
    smtp_host: "",
    smtp_port: "",
    smtp_user: "",
    smtp_pass: "",
    smtp_secure: true,
    from_name: "Pairfon",
    from_email: "no-reply@pairfon.com",
  });

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE_URL}/ops/email-settings`, { headers });
      if (res.data) {
        setCurrentSettings(res.data);
        setForm({
          provider: res.data.provider || "brevo",
          api_key: res.data.api_key || "",
          domain: res.data.domain || "",
          smtp_host: res.data.smtp_host || "",
          smtp_port: res.data.smtp_port || "",
          smtp_user: res.data.smtp_user || "",
          smtp_pass: res.data.smtp_pass || "",
          smtp_secure: res.data.smtp_secure !== false,
          from_name: res.data.from_name || "Pairfon",
          from_email: res.data.from_email || "no-reply@pairfon.com",
        });
      }
    } catch (err) {
      console.error("OpsEmailSettings fetch error", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setVerified(false);
  };

  const selectedProvider = PROVIDERS.find((p) => p.value === form.provider);
  const visibleFields = selectedProvider ? selectedProvider.fields : [];

  const handleVerify = async () => {
    setVerifying(true);
    setError("");
    setSuccess("");
    setVerified(false);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/ops/email-settings/verify`,
        form,
        { headers }
      );
      setVerified(true);
      setSuccess(res.data.message || "Connection verified.");
    } catch (err) {
      setError(err.response?.data?.error || "Connection failed. Check your credentials.");
    } finally {
      setVerifying(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      await axios.post(`${API_BASE_URL}/ops/email-settings`, form, { headers });
      setSuccess("Email settings saved successfully.");
      setVerified(true);
      fetchSettings();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to save settings.");
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!testEmail) {
      setError("Enter a test email address.");
      return;
    }
    setTesting(true);
    setError("");
    setSuccess("");
    try {
      const res = await axios.post(
        `${API_BASE_URL}/ops/email-settings/test`,
        { to: testEmail },
        { headers }
      );
      setSuccess(res.data.message || "Test email sent.");
    } catch (err) {
      setError(err.response?.data?.error || "Test email failed.");
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return <div className="ops-section"><p>Loading email settings...</p></div>;
  }

  return (
    <div className="ops-section">
      <div className="ops-section-header">
        <h3>Email Provider Settings</h3>
      </div>

      {currentSettings && (
        <div
          className="mb-4 p-3 rounded"
          style={{ background: "rgba(37,211,102,0.10)", border: "1px solid rgba(37,211,102,0.3)" }}
        >
          <strong>Current Provider:</strong>{" "}
          {PROVIDERS.find((p) => p.value === currentSettings.provider)?.label || currentSettings.provider}
          <br />
          <strong>From:</strong> {currentSettings.from_name} &lt;{currentSettings.from_email}&gt;
          <br />
          <small className="text-muted">
            Last updated: {new Date(currentSettings.updated_at).toLocaleString()}
          </small>
        </div>
      )}

      {!currentSettings && (
        <div
          className="mb-4 p-3 rounded"
          style={{ background: "rgba(245,181,68,0.12)", border: "1px solid rgba(245,181,68,0.35)" }}
        >
          <strong>No email provider configured.</strong> Emails (verification, password reset) will not
          be sent until you configure a provider below.
        </div>
      )}

      {error && <div className="alert alert-danger">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <form onSubmit={handleSave}>
        <div className="row">
          <div className="col-md-6 mb-3">
            <label className="form-label fw-bold">Email Provider</label>
            <select
              name="provider"
              className="form-select"
              value={form.provider}
              onChange={handleChange}
            >
              {PROVIDERS.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
            <small className="text-muted">{PROVIDER_HELP[form.provider]}</small>
          </div>
        </div>

        <hr />
        <h5 className="mb-3">Provider Credentials</h5>

        <div className="row">
          {visibleFields.includes("api_key") && (
            <div className="col-md-6 mb-3">
              <label className="form-label">{FIELD_LABELS.api_key}</label>
              <input
                type="password"
                className="form-control"
                name="api_key"
                value={form.api_key}
                onChange={handleChange}
                placeholder={FIELD_PLACEHOLDERS.api_key}
                required
              />
            </div>
          )}

          {visibleFields.includes("domain") && (
            <div className="col-md-6 mb-3">
              <label className="form-label">{FIELD_LABELS.domain}</label>
              <input
                type="text"
                className="form-control"
                name="domain"
                value={form.domain}
                onChange={handleChange}
                placeholder={FIELD_PLACEHOLDERS.domain}
              />
            </div>
          )}

          {visibleFields.includes("smtp_host") && (
            <div className="col-md-6 mb-3">
              <label className="form-label">{FIELD_LABELS.smtp_host}</label>
              <input
                type="text"
                className="form-control"
                name="smtp_host"
                value={form.smtp_host}
                onChange={handleChange}
                placeholder={FIELD_PLACEHOLDERS.smtp_host}
                required
              />
            </div>
          )}

          {visibleFields.includes("smtp_port") && (
            <div className="col-md-4 mb-3">
              <label className="form-label">{FIELD_LABELS.smtp_port}</label>
              <input
                type="number"
                className="form-control"
                name="smtp_port"
                value={form.smtp_port}
                onChange={handleChange}
                placeholder={FIELD_PLACEHOLDERS.smtp_port}
              />
            </div>
          )}

          {visibleFields.includes("smtp_user") && (
            <div className="col-md-6 mb-3">
              <label className="form-label">{FIELD_LABELS.smtp_user}</label>
              <input
                type="text"
                className="form-control"
                name="smtp_user"
                value={form.smtp_user}
                onChange={handleChange}
                placeholder={FIELD_PLACEHOLDERS.smtp_user}
              />
            </div>
          )}

          {visibleFields.includes("smtp_pass") && (
            <div className="col-md-6 mb-3">
              <label className="form-label">{FIELD_LABELS.smtp_pass}</label>
              <input
                type="password"
                className="form-control"
                name="smtp_pass"
                value={form.smtp_pass}
                onChange={handleChange}
                placeholder={FIELD_PLACEHOLDERS.smtp_pass}
                required
              />
            </div>
          )}

          {visibleFields.includes("smtp_secure") && (
            <div className="col-md-4 mb-3 d-flex align-items-end">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="smtp_secure"
                  id="smtpSecure"
                  checked={form.smtp_secure}
                  onChange={handleChange}
                />
                <label className="form-check-label" htmlFor="smtpSecure">
                  {FIELD_LABELS.smtp_secure}
                </label>
              </div>
            </div>
          )}
        </div>

        <hr />
        <h5 className="mb-3">Sender Identity</h5>

        <div className="row">
          <div className="col-md-4 mb-3">
            <label className="form-label">From Name</label>
            <input
              type="text"
              className="form-control"
              name="from_name"
              value={form.from_name}
              onChange={handleChange}
              placeholder="Pairfon"
            />
          </div>
          <div className="col-md-4 mb-3">
            <label className="form-label">From Email</label>
            <input
              type="email"
              className="form-control"
              name="from_email"
              value={form.from_email}
              onChange={handleChange}
              placeholder="no-reply@pairfon.com"
              required
            />
          </div>
        </div>

        <button
          type="button"
          className={`btn me-2 ${verified ? "btn-success" : "btn-outline-secondary"}`}
          onClick={handleVerify}
          disabled={verifying || saving}
        >
          {verifying ? "Verifying..." : verified ? "Verified" : "Verify Connection"}
        </button>
        <button className="btn btn-primary me-2" type="submit" disabled={saving || verifying}>
          {saving ? "Saving..." : "Save Settings"}
        </button>
        {!verified && (
          <small className="text-muted d-block mt-2">
            Credentials will be verified automatically when you save.
          </small>
        )}
      </form>

      <hr className="my-4" />

      <div className="ops-section-header">
        <h3>Send Test Email</h3>
      </div>
      <p className="text-muted">Send a test email to verify your configuration works.</p>
      <div className="row">
        <div className="col-md-4 mb-3">
          <input
            type="email"
            className="form-control"
            placeholder="test@example.com"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
          />
        </div>
        <div className="col-md-4 mb-3">
          <button
            className="btn btn-outline-primary"
            onClick={handleTest}
            disabled={testing || !currentSettings}
          >
            {testing ? "Sending..." : "Send Test"}
          </button>
        </div>
      </div>
    </div>
  );
}
